import React, {useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import Stack from '@mui/material/Stack';
import {HEADER_BOX_SX} from '../../const/headerBox';
import {useDispatch, useSelector} from 'react-redux';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    styled
} from '@mui/material';
import {
    confirmProfile,
    resetUpdatingStatus,
    retrieveBasicInfo,
    retrieveContactRelations,
    retrieveCountries,
    retrieveGroupsForBasicDetails,
    retrieveProfile,
    updateBasicInfo,
    updateStudentByParent
} from '../../ProfilesSlice';
import {showSnackbar} from '../../../../../AppLayout/ApplicationSlice';
import Typography from '@mui/material/Typography';
import {useParams} from 'react-router-dom';
import {useConfirm} from 'material-ui-confirm';
import useUser from '../../../../../utility/hooks/useUser';
import {formatDate} from '../../../../../utility/dateUtil';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import PropTypes from 'prop-types';
import {isValidUPN, validateEmail, validateFieldLength} from '../../../../../utility/validationUtil';
import useTextFields from '../../../../../utility/hooks/useTextFields';
import moment from 'moment/moment';
import {EditableFields} from '../../../../../shared-components/EditableFields';
import AdmicityAddressLookup from '../../../../../shared-components/AdmicityAddressLookup';
import PROFILE_TYPE from '../../../../../constants/profileTypes';
import AdmissionCategories from './AdmissionCategories';
import {withOutOfUse} from './admissionCategoryList';
import {useGetAdmissionCategoriesQuery} from '../../../../../api/services/lookupsService';
import ApplicantPhotoUpload from './ApplicantPhotoUpload';
import ProfileSectionHeading from '../ProfileSectionHeading';
import onNextFrame from '../../../../../utility/onNextFrame';
import {canEditSection, canViewSection} from './sectionPermissions';

const ActionButtonsBox = styled(Box)(({theme}) => ({
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'end',
    paddingTop: theme.spacing(2),
}));

const checkTextFieldsAreValid = fields =>
    !Object.values(fields)
        .some(({textFieldProps: props}) => props.error || ((props.value === '' || props.value === null || props.value === undefined) && props.required));

// Maria, 17 October 2026: the headings inside the white boxes at the top of the profile
// The home address as one value, field by field, ignoring spaces and capitals, to tell whether it has been changed.
const addressKey = address => (address
    ? ['apartment', 'houseName', 'houseNumber', 'street', 'district', 'town', 'county', 'postcode']
        .map(field => String(address[field] ?? '').replace(/\s+/g, '').toLowerCase())
        .concat(String(address.countryCode || address.country || '').toLowerCase()).join('|')
    : '');

const HEADER_HEADING_SX = {fontWeight: 700, fontSize: 16, lineHeight: 1.35, mb: 0.5};
// Maria, 18 October 2026: the first line of the names and the home address is level with the top of the photo and
// the student's name: the box's 12px padding and the photo's 6px.
const LEVEL_WITH_DETAILS_SX = {pt: '18px'};

// Maria, 18 October 2026: faint lines between the rows, and above the first and below the last
const ANSWER_GRID_SX = {
    display: 'grid',
gridTemplateColumns: 'max-content auto',
columnGap: 0,
rowGap: 0,
alignItems: 'stretch',
    '& > * > *': {py: 0.5, borderBottom: '1px solid #eef0f3'},
    '& > * > *:first-of-type': {pr: 1.5},
    '& > *:first-child > *': {borderTop: '1px solid #eef0f3'}
};

const BasicInfo = ({readonly}) => {
    const {user} = useUser();
    const [isEditMode, setIsEditMode] = useState(false);
    const {studentId} = useParams();
    const {
        selectedProfile,
        updatingSuccessful,
        houseGroups,
        yearGroups,
        formGroups,
        boarderGroups,
        currentSchool
    } = useSelector(state => state.profilesInfo);
    const dispatch = useDispatch();
    const confirm = useConfirm();
    const profileData = selectedProfile.basicInfo;
    const userHasParentRole = user.isParent();
    const [selectedAddress, setSelectedAddress] = useState(null);
    // Maria, 29 October 2026: nobody's address is changed for the others at it without asking, so neither choice is
    // made until the person chooses one ('current' or 'all')
    const [addressUpdateMode, setAddressUpdateMode] = useState(null);
    const [addressComponentKey, setAddressComponentKey] = useState(0);
    const [pendingPhoto, setPendingPhoto] = useState(null);

    const canEditPermissionModule = (permissionModule) => canEditSection(user, selectedProfile, permissionModule);
    const canViewPermissionModule = (permissionModule) => canViewSection(user, selectedProfile, permissionModule);

    const profileAddress = useMemo(() => {
        if (!profileData) return null;

        const addressFields = ['apartment', 'houseName', 'houseNumber', 'street', 'district', 'town', 'county', 'postcode', 'country', 'countryCode'];
        const address = {};

        addressFields.forEach(field => {
            if (profileData[field] !== undefined && profileData[field] !== null && profileData[field] !== '') {
                address[field] = profileData[field];
            }
        });

        return Object.keys(address).length > 0 ? address : null;
    }, [profileData]);

    const [originalAddress, setOriginalAddress] = useState(null);

    const addressLines = address => {
        if (!address) return [];
        const street = [address.houseNumber, address.street].filter(Boolean).join(' ');

        return [address.apartment, address.houseName, street, address.district, address.town, address.county,
            address.postcode, address.country].filter(Boolean);
    };

    const studentInfoTextFields = useMemo(() => {
        const dateOfBirth = profileData.dateOfBirth ? moment(profileData.dateOfBirth) : null;

        return profileData
            ? [
                {
                    id: 'profile_forename',
                    initialValue: profileData.forename,
                    // Maria, 17 October 2026: 'Legal Forename' and 'Legal Surname'
                    label: 'Legal Forename',
                    name: 'forename',
                    required: true,
                    disabled: !isEditMode,
                    validate: (value) => validateFieldLength(value, 60),
                },
                {
                    id: 'profile_middleName',
                    initialValue: profileData.middleName,
                    label: 'Middle Name',
                    name: 'middleName',
                    required: false,
                    disabled: !isEditMode,
                    validate: (value) => validateFieldLength(value, 60)
                },
                {
                    id: 'profile_surname',
                    initialValue: profileData.legalSurname,
                    label: 'Legal Surname',
                    name: 'legalSurname',
                    required: true,
                    disabled: !isEditMode,
                    validate: (value) => validateFieldLength(value, 60)
                },
                {
                    id: 'profile_preferredForename',
                    initialValue: profileData.chosenName,
                    label: 'Preferred Forename',
                    name: 'chosenName',
                    required: false,
                    disabled: !isEditMode,
                    validate: (value) => validateFieldLength(value, 60)
                },
                {
                    id: 'profile_preferredSurname',
                    initialValue: profileData.surname,
                    label: 'Preferred Surname',
                    name: 'surname',
                    required: true,
                    disabled: !isEditMode,
                    validate: (value) => validateFieldLength(value, 60)
                },
                {
                    id: 'profile_dateOfBirth',
                    initialValue: dateOfBirth && dateOfBirth.year() !== 1 ? dateOfBirth : null,
                    label: 'Date Of Birth',
                    name: 'dateOfBirth',
                    type: 'date',
                    required: true,
                    inputType: 'date',
                    disabled: !(isEditMode && !userHasParentRole),
                    minDate: moment(new Date('1970-01-01')),
                    nullableOnEmpty: true
                },
                {
                    id: 'profile_gender',
                    initialValue: profileData.gender,
                    label: 'Sex',
                    name: 'gender',
                    type: 'select',
                    select: true,
                    required: false,
                    disabled: !(isEditMode && !userHasParentRole),
                    nullableOnEmpty: true,
                    options: [{value: 'M', title: 'Male'}, {value: 'F', title: 'Female'}],
                },
                {
                    id: 'profile_email',
                    initialValue: profileData.email,
                    // Maria, 17 October 2026: shown under the home address, for on-roll students only
                    label: 'Student Email Address',
                    name: 'email',
                    required: false,
                    disabled: !(isEditMode && !userHasParentRole),
                    validate: validateEmail,
                    nullableOnEmpty: true
                }
            ]
            : [];
    }, [profileData, isEditMode]);

    const isAnyAddressFieldInProfileData = () => {
        const addressKeys = ['town', 'postcode', 'houseNumber', 'apartment'];
        return addressKeys.some((key) => key in profileData);
    };

    const canRenderRegistrationInformation = true;
    const canRenderAddress = isAnyAddressFieldInProfileData();
    const shouldRenderDetailsColumn = canRenderRegistrationInformation || canRenderAddress;
    const canViewFullApplicantRegistrationInformation = profileData.canViewApplicantRegistrationInformation !== false &&
        canViewPermissionModule('ApplicantRegistrationInformation');
    // AD-2419: viewing the section is not the same as editing it. Manage Access offers View only for every role, so an
    // applicant's Registration Information stays read-only on Edit.
    const canEditApplicantRegistrationInformation = canViewFullApplicantRegistrationInformation &&
        canEditPermissionModule('ApplicantRegistrationInformation');

    // Its own section with its own permission, because a school that hides Registration Information
    // for its applicants still wants the admissions categories in view, and the other way round.
    const canRenderAdmissionCategories = canViewPermissionModule('ApplicantAdmissionCategories');
    // Saving a child's categories needs Edit on the same row, so a person with View reads them only.
    const canEditAdmissionCategories = user.permissions.includes('Permission.ApplicantAdmissionCategories.Edit');

    const educationInfoTextFields = useMemo(() =>
        profileData
            ? [
                selectedProfile.isApplicant
                    ? {
                        id: 'profile_intakeGroup',
                        label: 'Intake Group',
                        name: 'intakeId',
                        type: 'select',
                        select: true,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.intakeId,
                        options: yearGroups.map(element => ({value: element.id, title: element.name})),
                        nullableOnEmpty: true
                    }
                    : {
                        id: 'profile_yearGroup',
                        label: 'Year Group',
                        name: 'yearOfEntryId',
                        type: 'select',
                        select: true,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.yearOfEntryId,
                        options: yearGroups.map(element => ({value: element.id, title: element.name})),
                        nullableOnEmpty: true
                    },
                ...(selectedProfile.personType === PROFILE_TYPE.ENQUIRY
                    ? [
                        {
                            id: 'profile_yearOfEntry',
                            label: 'Year of Entry',
                            name: 'enquiryAdmissionYear',
                            disabled: true,
                            initialValue: profileData.enquiryAdmissionYear
                        },
                        {
                            id: 'profile_enquirySeason',
                            label: 'Season',
                            name: 'enquirySeason',
                            disabled: true,
                            initialValue: profileData.enquirySeason
                        }
                    ]
                    : []),
                ...[
                    {
                        id: 'profile_form',
                        label: 'Form',
                        name: 'formId',
                        type: 'select',
                        select: true,
                        required: false,
                        parentEdit: false,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.formId,
                        options: formGroups.map(element => ({value: element.id, title: element.name})),
                        nullableOnEmpty: true
                    },
                    ...(currentSchool.hideHouseGroups
                        ? []
                        : [
                            {
                                id: 'profile_house',
                                label: 'House',
                                name: 'houseId',
                                type: 'select',
                                select: true,
                                required: false,
                                parentEdit: false,
                                disabled: !(isEditMode && !userHasParentRole),
                                initialValue: profileData.houseId,
                                options: houseGroups.map(element => ({value: element.id, title: element.name})),
                                nullableOnEmpty: true
                            }
                        ]),
                    ...(currentSchool.hideBoarderGroups
                        ? []
                        : [
                            {
                                id: 'profile_boarderStatus',
                                label: 'Boarder Status',
                                name: 'boarderId',
                                type: 'select',
                                select: true,
                                required: false,
                                parentEdit: false,
                                disabled: !(isEditMode && !userHasParentRole),
                                initialValue: profileData.boarderId,
                                options: boarderGroups.map(element => ({value: element.id, title: element.name})),
                                nullableOnEmpty: true
                            }
                        ]),
                    {
                        id: 'previousSchoolName',
                        label: 'Previous School Name',
                        name: 'previousSchoolName',
                        required: false,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.previousSchoolName
                    },
                    {
                        id: 'previousSchoolEmail',
                        label: 'Previous School Email',
                        name: 'previousSchoolEmail',
                        required: false,
                        validate: validateEmail,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.previousSchoolEmail
                    },
                    {
                        id: 'profile_upn',
                        label: 'UPN',
                        name: 'upn',
                        required: false,
                        disabled: !(isEditMode && !userHasParentRole),
                        initialValue: profileData.upn,
                        validate: (value) => ({
                            isValid: isValidUPN(value),
                            message: 'UPN\'s must use the format Annnnnnnnnnnn. The letters I,O and S are not allowed. Temporary UPN\'s have a trailing letter.'
                        }),
                    }
                ]
            ]
            : [], [profileData, isEditMode, currentSchool, yearGroups, formGroups, houseGroups, boarderGroups, userHasParentRole]);

    const {
        fields: studentInfoFields,
        getFieldValues: getStudentFieldValues
    } = useTextFields(studentInfoTextFields);
    const {
        fields: educationInfoFields,
        getFieldValues: getEducationFieldValues
    } = useTextFields(educationInfoTextFields);
    const visibleEducationInfoFields = useMemo(() => {
        if (!selectedProfile.isApplicant || canViewFullApplicantRegistrationInformation) {
            return educationInfoFields;
        }

        return Object.fromEntries(
            Object.entries(educationInfoFields)
                .filter(([, field]) => ['previousSchoolName', 'previousSchoolEmail'].includes(field.textFieldProps.name))
        );
    }, [educationInfoFields, selectedProfile.isApplicant, canViewFullApplicantRegistrationInformation]);

    // Maria, 17 October 2026: Registration Information is shown at the top of the profile, beside the outstanding
    // sections, while the profile is being viewed. The card below returns when someone clicks on 'Edit'.
    const [headerSlot, setHeaderSlot] = useState(null);
    const [headerBoxWidth, setHeaderBoxWidth] = useState(null);
    const [registrationBoxWidth, setRegistrationBoxWidth] = useState(null);
    // the width of the details beside the photo while viewing, kept while editing so the boxes do not move
    const [detailsWidth, setDetailsWidth] = useState(null);
    const [, setAddressBoxWidth] = useState(null);
    useEffect(() => {
        const box = document.querySelector('[data-testid="profile-header-box"]');
        const registration = document.querySelector('[data-testid="header-registration-information"]');
        if (!box || typeof ResizeObserver === 'undefined') return undefined;
        const categories = document.querySelector('[data-testid="header-admissions-categories"]');
        // Maria, 17 October 2026: the home address card runs from the left of the Registration Information box to the
        // right of the admissions categories box, when an applicant has categories.
        const measure = () => {
            // the widths are taken while viewing and kept while editing, so nothing moves
            if (isEditMode) return;
            setHeaderBoxWidth(box.offsetWidth);
            const address = document.querySelector('[data-testid="header-home-address"]');
            if (address) setAddressBoxWidth(address.offsetWidth);
            const details = document.querySelector('[data-testid="header-registration-details"]');
            if (details) setDetailsWidth(details.offsetWidth);
            if (!registration) {
                setRegistrationBoxWidth(null);
            } else if (categories) {
                setRegistrationBoxWidth(Math.round(categories.getBoundingClientRect().right - registration.getBoundingClientRect().left));
            } else {
                setRegistrationBoxWidth(registration.offsetWidth);
            }
        };
        const observer = new ResizeObserver(onNextFrame(measure));
        observer.observe(box);
        if (registration) observer.observe(registration);
        if (categories) observer.observe(categories);
        window.addEventListener('resize', measure);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [profileData, headerSlot, isEditMode]);
    // the space at the top of the profile can appear after this tab, so it is looked for again after each render
    const [detailsSlot, setDetailsSlot] = useState(null);
    const [photoSlot, setPhotoSlot] = useState(null);
    useEffect(() => {
        const photo = document.getElementById('profile-photo-slot');
        if (photo !== photoSlot) setPhotoSlot(photo);
        const slot = document.getElementById('profile-header-registration');
        if (slot !== headerSlot) setHeaderSlot(slot);
        const details = document.getElementById('profile-header-details');
        if (details !== detailsSlot) setDetailsSlot(details);
    });
    const {data: schoolAdmissionCategories = []} = useGetAdmissionCategoriesQuery();
    const headerCategories = withOutOfUse(schoolAdmissionCategories, profileData?.admissionCategories)
        .filter(category => (profileData?.admissionCategories ?? []).some(held => held.id === category.id));
    // Maria, 17 October 2026: each category on its own line, for example '4. Sibling in Catchment'.
    const headerCategoryLabel = category => `${category.number != null ? `${category.number}. ` : ''}${category.shortDescription}${category.outOfUse ? ' (out of use)' : ''}`;
    // Maria, 17 October 2026: hidden when no email address is recorded, as most primary school students do not have
    // one. A member of staff who clicks on 'Edit' can still add it.
    const showSchoolEmail = selectedProfile.personType === PROFILE_TYPE.STUDENT && Object.values(studentInfoFields).some(field => field.textFieldProps.name === 'email') &&
        (Boolean(profileData?.email) || (isEditMode && !userHasParentRole));
    // Maria, 17 October 2026: all five names in one column, then sex and date of birth
    // and the student's email address under them for on-roll students
    // (Maria, 17 October 2026: sex and date of birth are under the UPN, beside the photo)
    // (Maria, 18 October 2026: an on-roll student's email address is under the photo, where an applicant's previous
    // school is)
    // Maria, 18 October 2026: sex and date of birth under the names again
    const STUDENT_COLUMNS = [['forename', 'middleName', 'legalSurname', 'chosenName', 'surname', 'gender', 'dateOfBirth']];
    const studentEmailField = Object.values(studentInfoFields).filter(field => field.textFieldProps.name === 'email');
    const studentColumns = STUDENT_COLUMNS
        .map(names => names.filter(name => name !== 'email' || showSchoolEmail))
        .map(names => names.map(name => Object.values(studentInfoFields).find(field => field.textFieldProps.name === name)).filter(Boolean))
        .filter(column => column.length > 0);
    const studentDetailsWithoutEmail = Object.fromEntries(Object.entries(studentInfoFields)
        .filter(([, field]) => field.textFieldProps.name !== 'email'));
    // AD-2228: the straight-line distance from the school, for applicants only; staff are sent it, parents are not
    const distanceFromSchool = selectedProfile.isApplicant && profileData?.distanceFromSchool != null
        ? `${Number(profileData.distanceFromSchool).toFixed(2)} miles`
        : null;
    const showHeaderCategories = canRenderAdmissionCategories && selectedProfile.isApplicant &&
        ((profileData?.admissionCategories ?? []).length > 0 || (isEditMode && !userHasParentRole && canEditAdmissionCategories));
    const editRegistration = isEditMode && (!selectedProfile.isApplicant || canEditApplicantRegistrationInformation);
    // Maria, 18 October 2026: in the details and names boxes the answers line up after the longest question.
    // Each line gives its question and answer to the box's two-column grid (ANSWER_GRID_SX).
    const registrationLine = field => (
        <Box key={field.textFieldProps.name} sx={field.wrap ? {display: 'flex', gap: 1} : {display: 'contents'}}>
            <Box component="span" sx={{fontWeight: 600, whiteSpace: 'nowrap'}}>{field.textFieldProps.label}:</Box>
            {/* Maria, 19 October 2026: a long value goes onto a second line on a phone or small tablet */}
            <Box component="span" sx={{whiteSpace: {xs: 'normal', md: 'nowrap'}, overflowWrap: {xs: 'anywhere', md: 'normal'}}}>{registrationValue(field)}</Box>
        </Box>
    );
    const registrationValue = ({textFieldProps, otherProps}) => {
        if (textFieldProps.type === 'date') {
            return textFieldProps.value ? textFieldProps.value.format('DD-MMM-YYYY') : '';
        }
        if (textFieldProps.type === 'select') {
            return otherProps?.options?.find(x => x.value === textFieldProps.value)?.title ?? '';
        }

        return textFieldProps.value ?? '';
    };

    // Maria, 17 October 2026: UPN above the previous school details.
    const registrationOrder = field => (field.textFieldProps.name.startsWith('previousSchool') ? 1 : 0);
    const isPreviousSchool = field => field.textFieldProps.name.startsWith('previousSchool');
    const registrationFields = Object.values(selectedProfile.personType === PROFILE_TYPE.APPLICATION
        ? visibleEducationInfoFields
        : educationInfoFields)
        // Maria, 17 October 2026: Form and House are for on-roll students only; applicants have the intake group
        .filter(field => selectedProfile.personType !== PROFILE_TYPE.APPLICATION || !['formId', 'houseId'].includes(field.textFieldProps.name))
        .sort((a, b) => registrationOrder(a) - registrationOrder(b));

    const isAddressValid = useMemo(() => {
        if (!selectedAddress) return true;

        const fields = ['apartment', 'houseName', 'houseNumber', 'street', 'district', 'town', 'county', 'postcode'];
        const filled = fields.filter(f => selectedAddress?.[f] && selectedAddress[f].toString().trim() !== '');

        if (filled.length === 0) return true;

        const hasPostcode = Boolean(selectedAddress?.postcode && selectedAddress.postcode.toString().trim() !== '');
        const hasTownOrStreet = Boolean(
            (selectedAddress?.town && selectedAddress.town.toString().trim() !== '') ||
            (selectedAddress?.street && selectedAddress.street.toString().trim() !== '')
        );

        return hasPostcode && hasTownOrStreet;
    }, [selectedAddress]);

    const areAllTextFieldsValid =
        checkTextFieldsAreValid(studentInfoFields) &&
        checkTextFieldsAreValid(educationInfoFields);

    // A changed home address is saved only after choosing who it is changed for; the rest of the profile can be saved
    // without a choice while the address is unchanged
    const addressChanged = addressKey(selectedAddress) !== addressKey(originalAddress);
    const isFormValid = areAllTextFieldsValid && isAddressValid && (!addressChanged || addressUpdateMode !== null);

    useEffect(() => {
        dispatch(retrieveBasicInfo({ studentId, isParent: userHasParentRole })).then(() => {
            setAddressComponentKey(prev => prev + 1);
        });
        dispatch(retrieveProfile({ studentId }))
            .then(res => {
                dispatch(retrieveGroupsForBasicDetails({
                    isApplicant: res.payload?.personType === PROFILE_TYPE.APPLICATION
                }));
            });
    }, [dispatch, studentId, userHasParentRole]);

    useEffect(() => {
        if (selectedProfile.personType !== PROFILE_TYPE.ENQUIRY && selectedProfile.personType !== PROFILE_TYPE.NONE) {
            dispatch(retrieveCountries());
            dispatch(retrieveContactRelations());
        }
    }, [selectedProfile.personType]);

    useEffect(() => {
        if (profileData) {
            const currentAddressString = JSON.stringify(originalAddress);
            const newAddressString = JSON.stringify(profileAddress);

            if (currentAddressString !== newAddressString) {
                setOriginalAddress(profileAddress);
                if (!isEditMode) {
                    setSelectedAddress(profileAddress);
                }
            }
        }
    }, [profileData, profileAddress, isEditMode, originalAddress]);

    useEffect(() => {
        showNotification(updatingSuccessful);
        if (updatingSuccessful) {
            dispatch(resetUpdatingStatus());
            dispatch(retrieveBasicInfo({studentId: selectedProfile.id, isParent: userHasParentRole}));
            dispatch(retrieveProfile({studentId: selectedProfile.id}));
            setIsEditMode(false);
            setPendingPhoto(null);
            setAddressUpdateMode(null);
            setAddressComponentKey(prev => prev + 1);
        }

        return () => {
            dispatch(resetUpdatingStatus());
        };
    }, [updatingSuccessful]);

    const showNotification = success => success !== undefined && dispatch({
        true: () => showSnackbar({
            message: 'Basic student information updated successfully', severity: 'success'
        }),
        false: () => showSnackbar({
            message: 'Failed to update basic student information', severity: 'error'
        })
    }[success]());

    const handleSave = () => {
        const updateModeText = {
            all: 'This will update the address for all family members who share the same current address.',
            current: 'This will only update the address for this student.'
        }[addressUpdateMode] ?? '';

        confirm({
            title: 'Confirmation',
            description: (
                <Box>
                    <Typography>
                        {`I confirm that the information in the basic profile section of ${selectedProfile.name} ${selectedProfile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`}
                    </Typography>
                    {updateModeText && (
                        <Typography sx={{mt: 2, fontWeight: 'bold'}}>
                            {updateModeText}
                        </Typography>
                    )}
                </Box>
            ),
            confirmationText: 'Confirm'
        })
            .then(() => {
                const {photo: _photo, ...profileDetails} = profileData;
                const studentDetails = {
                    ...profileDetails,
                    ...getStudentFieldValues(),
                    ...getEducationFieldValues(),
                    ...selectedAddress,
                    updateAllLinkedAddresses: addressUpdateMode === 'all',
                    ...(pendingPhoto !== null ? {photo: pendingPhoto} : {}),
                };

                if (userHasParentRole) {
                    dispatch(updateStudentByParent({
                        studentId: selectedProfile.id,
                        id: studentDetails.id,
                        studentDetails
                    }));
                } else {
                    dispatch(updateBasicInfo({
                        studentId: selectedProfile.id,
                        id: studentDetails.id,
                        studentDetails
                    }));
                }
            })
            .catch(() => {
            });
    };

    const handleConfirm = () => {
        confirm({
            title: 'Confirmation',
            description: `I confirm that the information in the basic profile section of ${selectedProfile.name} ${selectedProfile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
            confirmationText: 'Confirm'
        })
            .then(() => {
                dispatch(confirmProfile({
                    studentId: selectedProfile.id,
                    type: STUDENT_PROFILE_CONFIRMATION_TYPES.Basic
                }));
            })
            .catch(() => {
            });
    };

    const handleCancel = () => {
        setIsEditMode(false);
        setPendingPhoto(null);
        setSelectedAddress(originalAddress);
        setAddressUpdateMode(null);
        setAddressComponentKey(prev => prev + 1);
    };

    const handleApplicantPhotoSelect = (photo) => {
        setPendingPhoto(photo);
    };

    const showPreviousSchool = selectedProfile.personType !== PROFILE_TYPE.STUDENT && registrationFields.some(isPreviousSchool);

    // Maria, 17 October 2026: the home address, in the top row of the profile beside the names, or in its own card
    const renderAddress = () => (
        <>
        {headerSlot
            ? <Box sx={HEADER_HEADING_SX}>Home Address</Box>
            : <ProfileSectionHeading>Home Address</ProfileSectionHeading>}
        {/* Maria, 17 October 2026: the address as plain lines until someone clicks on 'Edit'. */}
        {!isEditMode && (
            // Maria, 17 October 2026: the same font and size as the other boxes at the top of the profile
            <Stack data-testid="home-address-lines" rowGap={0.25} sx={{fontSize: 16, lineHeight: 1.35}}>
                {addressLines(selectedAddress || profileAddress).map(line => <Box component="span" key={line}>{line}</Box>)}
            </Stack>
        )}
        {/* Maria, 18 October 2026: as little space as possible in the address box while editing (only here; the
            address finder keeps its spacing on other screens) */}
        {isEditMode && <Box sx={{
            mt: 0.5,
            '& .MuiGrid-container': {marginTop: '0 !important', marginLeft: '-8px', width: 'calc(100% + 8px)'},
            '& .MuiGrid-item': {paddingTop: '8px !important', paddingLeft: '8px !important'},
            '& .MuiFormControlLabel-root': {my: 0.25},
            '& .MuiRadio-root': {py: 0.25}
        }}>
            <AdmicityAddressLookup
                key={addressComponentKey}
                onAddressSelect={(address) => {
                    setSelectedAddress(address);
                }}
                readonly={!isEditMode}
                initialValue={selectedAddress || profileAddress}
                defaultCountry={process.env.REACT_APP_DEFAULT_COUNTRY}
            />
            {isEditMode && selectedAddress && !isAddressValid && (
                <Typography variant="body2" color="error" sx={{mt: 1}}>
                    Address must have a postcode AND either a town or street
                </Typography>
            )}

            {isEditMode && (
                <>
                    <Divider sx={{my: 1}} />
                    <FormControl component="fieldset">
                        <RadioGroup
                            value={addressUpdateMode ?? ''}
                            onChange={(e) => setAddressUpdateMode(e.target.value)}
                        >
                            <FormControlLabel
                                value="current"
                                control={<Radio />}
                                // Maria, 18 October 2026: the two options without helper text
                                label="Update address for this student only"
                            />
                            <FormControlLabel
                                value="all"
                                control={<Radio />}
                                label="Update address for all family members who share the same current address"
                            />
                        </RadioGroup>
                    </FormControl>
                </>
            )}
        </Box>}
        </>
    );

    const showActionButtons = selectedProfile.personType !== PROFILE_TYPE.ENQUIRY && selectedProfile.personType !== PROFILE_TYPE.NONE;
    const actionButtons = (
        <>
        {
            profileData.editedAt && profileData.editedBy && <Typography mt={1}>Confirmed
                at {formatDate(profileData.editedAt)} by {profileData.editedBy}</Typography>
        }
        {isEditMode
            ? <>
                <Button
                    sx={{width: 150}}
                    variant="outlined"
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
                <Button
                    sx={{width: 150}}
                    disabled={!isFormValid}
                    variant="contained"
                    onClick={handleSave}
                >
                    Save
                </Button>
            </>
            : <>
                {!profileData.editedAt &&
                    <Button variant="contained" onClick={handleConfirm}>Confirm</Button>}
                {!readonly &&
                    <Button variant="contained"
                            sx={{width: 150}}
                            onClick={() => setIsEditMode(true)}>
                        Edit
                    </Button>}
            </>}
        </>
    );

    return (<>
        {profileData && <>
            <Box
                display="flex"
                flexDirection={{
                    xs: 'column',
                    sm: 'column',
                    md: 'row',
                    lg: 'row',
                    xl: 'row'
                }}
                // Maria, 17 October 2026: 8px between the white boxes
                gap={1}
            >
                {/* Maria, 17 October 2026: the names, date of birth and sex are in the row at the top of the profile.
                    This card is only used if that row is missing. */}
                {!headerSlot && <Box sx={{flex: 1, minWidth: 0}}>
                    <Card variant="outlined">
                        <CardContent>
                            {/* Maria, 17 October 2026: the photo and name are at the top of the profile, above the tabs.
                                An applicant's photo can still be changed here while the profile is being edited. */}
                            {(selectedProfile.isApplicant && !readonly && isEditMode) && (
                                <Box sx={{mb: 1}}>
                                    <ApplicantPhotoUpload
                                        photo={pendingPhoto ?? selectedProfile.photo}
                                        alt={`${selectedProfile.name} ${selectedProfile.surname}`}
                                        canUpload
                                        onPhotoSelect={handleApplicantPhotoSelect}
                                    />
                                </Box>
                            )}
                            <EditableFields fields={studentDetailsWithoutEmail} edit={isEditMode}/>
                        </CardContent>
                    </Card>
                </Box>}
                {shouldRenderDetailsColumn && (
                    // Maria, 17 October 2026: a balanced page. Registration Information and the home address side by
                    // side on a wide screen, with the admissions categories under both.
                    <Box
                        flex={2}
                        sx={{
                            display: 'grid',
                            // Maria, 17 October 2026: while the profile is being viewed, the home address card has the
                            // same left edge and width as the Registration Information box above it.
                            gridTemplateColumns: headerSlot
                                ? '1fr'
                                : registrationBoxWidth
                                ? {xs: '1fr', md: `${registrationBoxWidth}px`}
                                : {xs: '1fr', lg: '1fr 1fr'},
                            gap: 1,
                            alignContent: 'start'
                        }}
                    >
                        {/* Maria, 17 October 2026: the year group, form, boarder status and UPN beside the photo, and the
                            previous school details in their own white box. 'Registration Information' stays as the name
                            in Manage Access. When someone clicks on 'Edit', the same details become fields in the same
                            places, so nothing moves. */}
                        {/* Maria, 17 October 2026: an applicant's photo is changed over the photo at the top */}
                        {photoSlot && isEditMode && selectedProfile.isApplicant && !readonly && createPortal(
                            <ApplicantPhotoUpload
                                photo={pendingPhoto ?? selectedProfile.photo}
                                alt={`${selectedProfile.name} ${selectedProfile.surname}`}
                                canUpload
                                onPhotoSelect={handleApplicantPhotoSelect}
                            />,
                            photoSlot
                        )}
                        {detailsSlot && createPortal(
                            isEditMode
                                ? <Box sx={{width: detailsWidth ? `${detailsWidth}px` : 240}} data-testid="header-registration-details">
                                    <EditableFields fields={registrationFields.filter(field => !isPreviousSchool(field))} edit={editRegistration}/>
                                    {/* Maria, 19 October 2026: an on-roll student's email address under the UPN */}
                                    {showSchoolEmail && !userHasParentRole && <EditableFields fields={studentEmailField} edit/>}
                                    {/* Maria, 19 October 2026: the previous school and admissions categories under the UPN */}
                                    {showPreviousSchool && <EditableFields fields={registrationFields.filter(isPreviousSchool)} edit={editRegistration}/>}
                                    {showHeaderCategories && (
                                        <Box sx={{mt: 1}} data-testid="header-admissions-categories">
                                            <Box sx={HEADER_HEADING_SX}>Admission Category</Box>
                                            <AdmissionCategories
                                                studentId={studentId}
                                                selected={profileData.admissionCategories}
                                                readonly={userHasParentRole || !canEditAdmissionCategories}
                                            />
                                        </Box>
                                    )}
                                    {distanceFromSchool && (
                                        <Box sx={{mt: 1}} data-testid="header-distance-from-school">
                                            <Box sx={HEADER_HEADING_SX}>Distance from School</Box>
                                            <Typography variant="body2">{distanceFromSchool}</Typography>
                                        </Box>
                                    )}
                                </Box>
                                // Maria, 19 October 2026: on a phone each label above its answer, so the answers have room
                                : <Box sx={{...ANSWER_GRID_SX, fontSize: 16, lineHeight: 1.35, gridTemplateColumns: {xs: '1fr', md: 'max-content auto'}, '& > * > *:first-of-type': {pr: 1.5, borderBottom: {xs: 'none', md: '1px solid #eef0f3'}, pb: {xs: 0, md: 0.5}}}} data-testid="header-registration-details">
                                    {registrationFields.filter(field => !isPreviousSchool(field)).map(registrationLine)}
                                    {/* Maria, 19 October 2026: an on-roll student's email address under the UPN */}
                                    {showSchoolEmail && (
                                        <Box sx={{display: 'contents'}} data-testid="header-student-email">
                                            <Box component="span" sx={{fontWeight: 600, whiteSpace: 'nowrap'}}>Student Email Address:</Box>
                                            <Box component="span" sx={{overflowWrap: 'anywhere'}}>{profileData.email}</Box>
                                        </Box>
                                    )}
                                    {/* Maria, 19 October 2026: under the UPN, the previous school's name and email address, and
                                        the admissions categories as their numbers, for example '1, 3, 4' */}
                                    {showPreviousSchool && registrationFields.filter(isPreviousSchool).map(field => registrationLine({
                                        ...field,
                                        textFieldProps: {...field.textFieldProps, label: field.textFieldProps.name === 'previousSchoolName' ? 'Previous School Name' : 'Previous School Email'}
                                    }))}
                                    {showHeaderCategories && (
                                        <Box sx={{display: 'contents'}} data-testid="header-admissions-categories">
                                            <Box component="span" sx={{fontWeight: 600, whiteSpace: 'nowrap'}}>Admission Category:</Box>
                                            <Box component="span" title={headerCategories.map(headerCategoryLabel).join(', ')}>
                                                {headerCategories.map(category => category.number ?? category.shortDescription).join(', ')}
                                            </Box>
                                        </Box>
                                    )}
                                    {distanceFromSchool && (
                                        <Box sx={{display: 'contents'}} data-testid="header-distance-from-school">
                                            <Box component="span" sx={{fontWeight: 600, whiteSpace: 'nowrap'}}>Distance from School:</Box>
                                            <Box component="span">{distanceFromSchool}</Box>
                                        </Box>
                                    )}
                                </Box>,
                            detailsSlot
                        )}
                        {headerSlot && createPortal(
                            <Stack direction="row" alignItems="stretch" gap={1} flexWrap={{xs: 'wrap', md: 'nowrap'}} sx={{width: '100%'}}>
                                {/* Maria, 17 October 2026: the names, date of birth and sex next to the photo box */}
                                <Box
                                    data-testid="header-student-details"
                                    sx={{...HEADER_BOX_SX, ...LEVEL_WITH_DETAILS_SX, flex: {xs: '1 1 100%', md: '1 1 0'}, minWidth: 0}}
                                >
                                    {isEditMode
                                        ? <>
                                            <Stack direction={{xs: 'column', sm: 'row'}} columnGap={2}>
                                                {studentColumns.map((column, index) => (
                                                    <Box key={index} sx={{width: '100%'}}>
                                                        <EditableFields fields={column} edit/>
                                                    </Box>
                                                ))}
                                            </Stack>
                                            {/* Maria, 19 October 2026: the home address under the date of birth, in the same box */}
                                            {canRenderAddress && <Box sx={{mt: 1}} data-testid="header-home-address">{renderAddress()}</Box>}
                                        </>
                                        : <Stack direction={{xs: 'column', sm: 'row'}} columnGap={4} sx={{fontSize: 16, lineHeight: 1.35}}>
                                            {studentColumns.map((column, index) => (
                                                <Box key={index} sx={{
                                                    ...ANSWER_GRID_SX,
                                                    // Maria, 19 October 2026: the space after each label narrows before the address goes onto a second line
                                                    '& > * > *:first-of-type': {pr: 'clamp(12px, 2.2vw, 32px)'}
                                                }}>
                                                    {column.map(registrationLine)}
                                                    {/* Maria, 19 October 2026: 'Home Address' under 'Date Of Birth', with the
                                                        address in the column of answers */}
                                                    {index === studentColumns.length - 1 && canRenderAddress && (
                                                        <Box sx={{display: 'contents'}} data-testid="header-home-address">
                                                            <Box component="span" sx={{fontWeight: 600, whiteSpace: 'nowrap'}}>Home Address:</Box>
                                                            {/* Maria, 19 October 2026: the address on one line, going onto the next
                                                                line between its parts only when the box has no more room */}
                                                            <Box component="span" data-testid="home-address-lines">
                                                                {addressLines(selectedAddress || profileAddress).map((line, at, all) => (
                                                                    <Box component="span" key={line} sx={{whiteSpace: 'nowrap'}}>{line}{at < all.length - 1 ? ', ' : ''}</Box>
                                                                )).reduce((parts, part) => (parts.length ? [...parts, ' ', part] : [part]), [])}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            ))}
                                        </Stack>}
                                </Box>
                            </Stack>,
                            headerSlot
                        )}
                        {/* Maria, 17 October 2026: under the photo, one white box with the previous school (its name and
                            email address under a 'Previous School' heading) and an applicant's admissions categories.
                            Maria, 18 October 2026: on-roll students have their email address here instead. */}
                        {headerSlot && (
                        // Maria, 19 October 2026: the Previous School and Admissions Categories boxes the same height
                        <Stack direction="row" alignItems="stretch" gap={1} flexWrap="wrap">
                        {/* Maria, 18 October 2026: the buttons under the home address, lined up with its edges */}
                        {showActionButtons && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={1}
                                flexWrap="wrap"
                                data-testid="basic-action-buttons"
                                // Maria, 18 October 2026: on the left, and smaller
                                sx={{
                                    alignSelf: 'flex-start',
                                    width: {xs: '100%', md: 'auto'},
                                    '& > .MuiButton-root': {flex: '0 0 auto', width: 'auto', minWidth: 100, py: 0.5, px: 2, fontSize: '0.8125rem'}
                                }}
                            >
                                {actionButtons}
                            </Stack>
                        )}
                        </Stack>
                        )}
                        {!headerSlot && <Card variant="outlined">
                            <CardContent>
                                <ProfileSectionHeading>Registration Information</ProfileSectionHeading>
                                <EditableFields
                                    fields={selectedProfile.personType === PROFILE_TYPE.APPLICATION
                                        ? visibleEducationInfoFields
                                        : educationInfoFields}
                                    edit={isEditMode && (!selectedProfile.isApplicant || canEditApplicantRegistrationInformation)}
                                />
                            </CardContent>
                        </Card>}

                        {/* Maria, 17 October 2026: never on an on-roll profile, and on an applicant profile only when a
                            category has been ticked, or while a member of staff who can tick them is editing. */}
                        {canRenderAdmissionCategories && selectedProfile.isApplicant &&
                            !headerSlot &&
                            ((profileData.admissionCategories ?? []).length > 0 ||
                                (isEditMode && !userHasParentRole && canEditAdmissionCategories)) && (
                            <Card variant="outlined" sx={{gridColumn: '1 / -1', order: 3}}>
                                <CardContent>
                                    <ProfileSectionHeading>Admissions Categories</ProfileSectionHeading>
                                    <AdmissionCategories
                                        studentId={studentId}
                                        selected={profileData.admissionCategories}
                                        readonly={!isEditMode || userHasParentRole || !canEditAdmissionCategories}
                                    />
                                </CardContent>
                            </Card>
                        )}

                        {canRenderAddress && !headerSlot && (<Card variant="outlined">
                            <CardContent>
                                {renderAddress()}
                            </CardContent>
                        </Card>)}
                    </Box>
                )}
            </Box>
            {
                !headerSlot && showActionButtons
                    // Maria, 17 October 2026: the buttons start level with the left of the home address card
                    ? <ActionButtonsBox sx={{justifyContent: {xs: 'end', md: 'flex-start'}, pl: {md: !headerSlot && headerBoxWidth ? `${headerBoxWidth + 32}px` : 0}}}>
                        {actionButtons}
                    </ActionButtonsBox>
                    : null
            }
        </>}
    </>);
};

BasicInfo.propTypes = {
    readonly: PropTypes.bool
};

export default BasicInfo;
